import { z } from 'zod';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * Workshop bookings (PRD → Maintenance → Flow; gitaumoses4/chukua#169): a truck is booked into a garage, or the
 * in-house workshop, for a window. A booking is never edited; a move is a new booking that supersedes the old one,
 * and a cancel closes it with a reason. A booking that overlaps a committed trip is flagged and saved only when the
 * caller sends `confirmConflicts`; the trips it was confirmed against stay on the booking.
 */

export const WORKSHOP_BOOKING_STATES = ['live', 'cancelled', 'superseded'] as const;
export type WorkshopBookingState = (typeof WORKSHOP_BOOKING_STATES)[number];

const bookingWindow = {
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
};

const endsAfterStart = { message: 'The booking must end after it starts', path: ['endsAt'] };

export const workshopBookingInputSchema = z
  .object({
    truckId: z.uuid(),
    /** Left out for the in-house workshop, which is `isInHouse: true`; exactly one of the two. */
    garageId: z.uuid().optional(),
    isInHouse: z.boolean().default(false),
    workOrderId: z.uuid().optional(),
    ...bookingWindow,
    confirmConflicts: z.boolean().default(false),
  })
  .refine((input) => input.isInHouse !== (input.garageId !== undefined), { message: 'Name a garage, or book the in-house workshop', path: ['garageId'] })
  .refine((input) => input.endsAt > input.startsAt, endsAfterStart);
export type WorkshopBookingInput = z.output<typeof workshopBookingInputSchema>;

/** `POST /workshop-bookings/:id/move`: the truck, workshop and work order stay; only the window changes. */
export const moveWorkshopBookingInputSchema = z
  .object({ ...bookingWindow, confirmConflicts: z.boolean().default(false) })
  .refine((input) => input.endsAt > input.startsAt, endsAfterStart);
export type MoveWorkshopBookingInput = z.output<typeof moveWorkshopBookingInputSchema>;

export const cancelWorkshopBookingInputSchema = z.object({ reason: z.string().trim().min(1).max(500) });
export type CancelWorkshopBookingInput = z.output<typeof cancelWorkshopBookingInputSchema>;

/** `GET /workshop-bookings` — search matches the truck's registration. `from`/`to` keep bookings that touch the range. */
export const workshopBookingListQuerySchema = defineListQuery({
  sortFields: ['startsAt', 'createdAt'],
  defaultSort: { sort: 'startsAt', order: 'asc' },
  filters: {
    truckId: listFilter(z.uuid()),
    garageId: listFilter(z.uuid()),
    state: listFilter(z.enum(WORKSHOP_BOOKING_STATES)),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
  },
});
export type WorkshopBookingListQuery = z.infer<typeof workshopBookingListQuerySchema>;

export interface WorkshopBookingView {
  id: string;
  truckId: string;
  truckRegistration: string;
  garageId: string | null;
  /** The garage's name, or "In-house workshop". */
  workshopName: string;
  isInHouse: boolean;
  workOrderId: string | null;
  startsAt: string;
  endsAt: string;
  state: WorkshopBookingState;
  endedAt: string | null;
  endReason: string | null;
  supersedesId: string | null;
  conflictsConfirmedTripIds: string[];
  createdAt: string;
}

/** A trip a booking runs into, named so the 409 can say which. */
export interface BookingTripConflict {
  tripId: string;
  tripNumber: string;
  startsAt: string;
  /** Null for a trip with no planned window: it holds the truck until it is delivered. */
  endsAt: string | null;
}

export const TRUCK_SCHEDULE_ENTRY_KINDS = ['booking', 'trip'] as const;
export type TruckScheduleEntryKind = (typeof TRUCK_SCHEDULE_ENTRY_KINDS)[number];

export interface TruckScheduleEntry {
  kind: TruckScheduleEntryKind;
  id: string;
  /** The workshop's name for a booking, the trip number for a trip. */
  label: string;
  startsAt: string;
  endsAt: string | null;
  /** True for a live booking that overlaps a trip, and for the trip it overlaps. */
  hasConflict: boolean;
}

/** `GET /trucks/:id/schedule` — a range of the truck's calendar, bookings and trips in order. */
export const truckScheduleQuerySchema = z
  .object({ from: z.coerce.date(), to: z.coerce.date() })
  .refine((query) => query.to > query.from, { message: 'The range must end after it starts', path: ['to'] });
export type TruckScheduleQuery = z.output<typeof truckScheduleQuerySchema>;

export const nextFreeWindowInputSchema = z.object({
  durationMinutes: z
    .number()
    .int()
    .min(15)
    .max(60 * 24 * 14),
  dueBy: z.coerce.date(),
});
export type NextFreeWindowInput = z.output<typeof nextFreeWindowInputSchema>;

export interface NextFreeWindowView {
  window: { startsAt: string; endsAt: string } | null;
  /** What stands in the way when there is no window: the busy periods before `dueBy`. */
  blockedBy: TruckScheduleEntry[];
}
